import { httpClient } from '@/domains/core/httpClient/httpClient';
import { getAppSetting } from '@/domains/core/settings/appSettings';
import { getPlatform, isB2B } from '@/domains/core/settings/utils';

import type {
  MainAttributeFromApi,
  Price,
  PriceFromApi,
  ProductToCompareFromApi,
  ProductToCompareI,
} from '../types';

/* eslint-disable camelcase */
const fromHttpParserPrice = (price: PriceFromApi): Price => ({
  vatRate: price.vat_rate,
  amountWithVat: price.amount_with_vat,
  amountWithoutVat: price.amount_without_vat,
  measurementUnit: price.measurement_unit,
});

const fromHttpParserMainAttributes = (
  attributesFromApi: MainAttributeFromApi[],
  uniqueAttributeNames: string[],
) =>
  uniqueAttributeNames.map(
    (attrName) =>
      attributesFromApi.find((attribute) => attribute.name === attrName) || {
        name: attrName,
        value: '–',
      },
  );

const fromHttpParser = (
  product: ProductToCompareFromApi,
  uniqueAttributeNames: string[],
): ProductToCompareI => ({
  productId: product.id,
  articleId: product.article_id,
  modelId: product.model_id,
  title: product.title,
  imageUrl: product.media.thumbnail_url,
  slug: product.slug,
  sellerName: product.seller_name || '',
  variants: product.variants,
  brand: {
    id: product.brand?.id,
    name: product.brand?.name,
    imageUrl: product.brand?.image_url,
  },
  price: {
    currency: product.price.currency,
    retailPrice: fromHttpParserPrice(product.price.retail_price),
    currentPrice: fromHttpParserPrice(product.price.current_price),
    measurementPrice: fromHttpParserPrice(product.price.measurement_price),
    discountPercentageForPrimaryPrice:
      product.price.discount_percentage_for_primary_price,
    measurementPriceIsPrimaryPrice:
      product.price.measurement_price_is_primary_price,
    pricePlusShipping: product.price.price_plus_shipping,
  },
  rating: {
    average: product.rating.average,
    totalRatings: product.rating.total_ratings,
  },
  delivery: {
    price: product.delivery?.cost?.price || 0,
    isFree: product.delivery?.cost?.is_free || false,
  },
  mainAttributes: fromHttpParserMainAttributes(
    product.main_attributes,
    uniqueAttributeNames,
  ),
  isBestQualityOffer: product.is_best_quality_offer,
  isCompetitive: product.is_competitive,
  isHighlight: product.is_highlight,
  isTopSale: product.is_top_sale,
  hasVariant: product.variants.total > 0,
  coupon: product.coupons
    .slice(0, 1)
    .map((coupon) => ({
      code: coupon.code,
      isPercent: coupon.is_percent,
      value: coupon.value,
    }))
    .at(0),
  promoTags: product.promo_tags || [],
});

interface ApiResponse {
  data: {
    content: ProductToCompareFromApi[];
  };
}

export const getProductsToCompare = (modelId: number, categoryId: number) =>
  httpClient
    .get(`/api/v1/pro-product-comparator/products`, {
      baseURL: getAppSetting('MS_API_URL'),
      params: {
        model_id: modelId,
        platform: getPlatform(),
        market: isB2B() ? 'b2b' : 'b2c',
        category_id: categoryId,
      },
    })
    .then((res: ApiResponse): ProductToCompareI[] => {
      const products = res.data.content.slice(0, 5);

      const attributeCounts = products.reduce(
        (attrCounts: AttributesWithCounts, product) => {
          product.main_attributes.forEach((a) => {
            // if the attribute name is not in the object, create it before incrementing
            if (!attrCounts[a.name]) {
              attrCounts[a.name] = 0;
            }
            attrCounts[a.name]++;
          });
          return attrCounts;
        },
        {},
      );

      Object.entries(attributeCounts).forEach(([key, val]) => {
        if (val < 3) {
          delete attributeCounts[key];
        }
      });

      const uniqueAttributeNames = Object.keys(attributeCounts).slice(0, 10);

      const parsedProducts = products.map((product) =>
        fromHttpParser(product, uniqueAttributeNames),
      );

      return parsedProducts;
    });

type AttributesWithCounts = Record<string, number>;
